import api from "./api";
import helpers from "./helpers";

const approxPrefix = {
  de: "rund",
  en: "roughly",
};

const placeOfHomePrefix = {
  de: "Wohnort für",
  en: "Place of home for",
};

function getPortraitUrl(figure) {
  const images = figure.images;
  if (!images || images.length === 0) return "/img/profile.jpg";

  // Handle nested array structure: images: [[{url: "..."}], null]
  // Exclude images flagged "Bild nicht verwenden" (disabled) in the CMS
  const flatImages = images
    .flat()
    .filter((img) => img !== null && typeof img === "object" && !img.disabled);

  if (flatImages.length === 0) return "/img/profile.jpg";

  // Check for images with either direct url or file.url structure
  const validImages = flatImages.filter((image) => image.url || image.file?.url);
  if (validImages.length === 0) return "/img/profile.jpg";

  const markedAsPortrait = validImages.find((image) => image.portrait);
  const portrait = markedAsPortrait || validImages[0];

  // Handle both structures: direct url or file.url
  const url = portrait.url || portrait.file?.url;
  return api.getAssetUrl(url, { preset: "postcard" });
}

function getPortraitAlt(figure) {
  const images = figure.images;
  if (!images || images.length === 0) return null;

  // Handle nested array structure from memorials API: images: [[{alt: "..."}], null]
  // Flatten the structure if needed and exclude disabled ("Bild nicht verwenden") images
  const flatImages = images
    .flat()
    .filter((img) => img !== null && typeof img === "object" && !img.disabled);

  if (flatImages.length === 0) return null;

  const markedAsPortrait = flatImages.find((image) => image.portrait);
  const portrait = markedAsPortrait || flatImages[0];

  if (!portrait) return null;

  return portrait.file?.alt || portrait.alt || null;
}

function calculatePlaceOfHomePeriods(stops) {
  return stops.forEach((stop, index) => {
    const type = stop.stop_type.value;

    // we only need to calculate the period for place_of_home stops
    if (type !== "place_of_home") return;

    let startEarlist;
    let startLatest;
    let endEarliest;
    let endLatest;

    const previousStop = stops[index - 1];
    const previousType = previousStop ? previousStop.stop_type.value : null;
    const nextStop = stops[index + 1];
    const nextType = nextStop ? nextStop.stop_type.value : null;

    if (previousType === "trip" || previousType === "stop") {
      startEarlist = previousStop.end_earliest;
      startLatest = previousStop.end_latest;

      if (nextType === "trip" || nextType === "stop") {
        endEarliest = nextStop.start_earliest;
        endLatest = nextStop.start_latest;
      } else {
        endEarliest = stop.end_earliest;
        endLatest = stop.end_latest;
      }
    } else {
      const nextStop = stops[index + 1];

      startEarlist = stop.start_earliest;
      startLatest = stop.start_latest;

      if (nextStop) {
        endEarliest = nextStop.start_earliest;
        endLatest = nextStop.start_latest;
      } else {
        endEarliest = stop.end_earliest;
        endLatest = stop.end_latest;
      }
    }

    const durationObject = helpers.getDurationObject(startEarlist, endLatest);
    const durationInDays = durationObject.asDays();
    const durationInMonths = durationObject.asMonths();

    stop.calculatedPeriod = {
      start_earliest: startEarlist,
      start_latest: startLatest,
      end_earliest: endEarliest,
      end_latest: endLatest,
      duration: {
        inDays: durationInDays,
        inMonths: durationInMonths,
      },
    };
  });
}

function getDuration(stop, locale) {
  const startEarliest = stop.start_earliest;
  const startLatest = stop.start_latest;
  const endEarliest = stop.end_earliest;
  const endLatest = stop.end_latest;
  const type = stop.stop_type.value;

  let durationStart;
  let durationEnd;
  let isExact = false;

  if (startEarliest === startLatest && endEarliest === endLatest) {
    durationStart = startEarliest;
    durationEnd = endEarliest;
    isExact = true;
  } else if (
    startEarliest !== startLatest &&
    endEarliest !== endLatest &&
    startEarliest === endEarliest &&
    startLatest === endLatest
  ) {
    durationStart = startEarliest;
    durationEnd = endLatest;
  } else {
    durationStart = helpers.getDateMidpoint(startEarliest, startLatest);
    durationEnd = helpers.getDateMidpoint(endEarliest, endLatest);
  }

  const durationObject = helpers.getDurationObject(durationStart, durationEnd);
  const durationInDays = durationObject.asDays();
  const durationInMonths = durationObject.asMonths();
  let durationString = durationObject
    .locale(locale)
    .humanize({ d: 26, M: 11 })
    .replace("a ", "1 ")
    .replace("ein ", "1 ");

  if (durationInMonths > 1 && !isExact) {
    durationString = `${approxPrefix[locale]} ${durationString}`;
  }

  if (type === "place_of_home") {
    durationString = `${placeOfHomePrefix[locale]} ${durationString}`;
  }

  // duration smaller than 1 means that the data has an error in the data
  // so we dont show a duration string
  if (durationInDays < 1) {
    durationString = "";
  }

  return {
    inDays: durationInDays,
    inMonths: durationInMonths,
    string: durationString,
    isExact,
  };
}

export default {
  getPortraitUrl,
  getPortraitAlt,
  getDuration,
  calculatePlaceOfHomePeriods,
};
